import { add } from 'date-fns/add'
import { createEffect } from 'effector'
import { nanoid } from 'nanoid'
import type { z } from 'zod'

import type { PortalProfile } from '@portal/portal-api'

import { $portalProfile } from '@/entities/profile/portal'
import { constants } from '@/shared/config'
import { timeout } from '@/shared/lib/time'
import { mapIfDefined, toSeconds } from '@/shared/lib/types'

import { createChatAPIEffect } from './effect'
import {
    type ChannelEvent,
    type HistoryResponse,
    type LoginRequest,
    type LoginResponse,
    type Message,
    type MessageTranslateRequest,
    type MessageTranslateResponse,
    MessageTranslateResponseSchema,
    type SystemEventSchema,
} from './protocol'
import {
    type ChannelJoinRequest,
    type ChannelLeaveRequest,
    type ChannelSlowmodeRequest,
    type ChatAPI,
    ChatAPIError,
    type HistoryRequest,
    type HTTPResponse,
    type MessageDeleteRequest,
    type MessagePublishRequest,
    type MessageReactionRequest,
    type MessageReportRequest,
    type UserMuteRequest,
    type UserUnmuteRequest,
} from './types'

export class ChatAPIMock implements ChatAPI {
    broadcast: Record<string, BroadcastChannel> = {}
    broadcastServer: BroadcastChannel = new BroadcastChannel('taonga-chat-server-channel')
    messages: Message[] = [...history2]
    token: string | null = null

    setToken = createEffect((token: string | null) => {
        this.token = token
    })

    login = createChatAPIEffect<LoginRequest, LoginResponse>((request) => {
        const profile = $portalProfile.getState() as PortalProfile
        return Promise.resolve(
            ok<LoginResponse>({
                user: {
                    user_id: profile.publicId,
                    token: JSON.stringify(profile),
                    role: 'admin',
                    // role: 'moderator',
                },
                channels: [
                    { name: constants.CHAT_CHANNEL_DEFAULT },
                    // добавлялка
                    { name: 'public:addme' },
                    // языковой канал
                    { name: `public:lang_${request.lang}` },
                ],
                chat_admins: [
                    {
                        user_id: '3',
                        role: 'admin',
                    },
                    {
                        user_id: '2050106016516387',
                        role: 'moderator',
                    },
                ],
                user_mutes: [
                    {
                        user_id: '3-0',
                        reason_code: 'spam',
                        create_time: new Date(),
                        update_time: new Date(),
                        expire_time: null,
                    },
                    {
                        user_id: '4-0',
                        reason_code: 'toxic_behavior',
                        create_time: new Date(),
                        update_time: new Date(),
                        expire_time: add(new Date(), { days: 7 }),
                    },
                ],
                chat_settings: {
                    maintenance: {
                        enabled: false,
                    },
                    message_limit: {
                        user: constants.CHAT_MESSAGE_MAX_LENGTH_USER,
                        admin: constants.CHAT_MESSAGE_MAX_LENGTH_ADMIN,
                    },
                },
            })
        )
    })

    userMute = createChatAPIEffect(async (request: UserMuteRequest) => {
        await timeout(300)

        this.postSystemEvent('system:admin', {
            type: 'user_muted',
            payload: {
                user_id: request.user_id,
                reason_code: request.reason_code,
                create_time: new Date().toISOString(),
                update_time: new Date().toISOString(),
                expire_time: request.expire_time?.toISOString() ?? null,
            },
        })

        return ok()
    })

    userUnmute = createChatAPIEffect(async (request: UserUnmuteRequest) => {
        await timeout(300)

        this.postSystemEvent('system:admin', {
            type: 'user_unmuted',
            payload: {
                user_id: request.user_id,
                reason_message: request.reason_message,
            },
        })

        return ok()
    })

    messagePublish = createChatAPIEffect(async (request: MessagePublishRequest) => {
        await timeout(300)

        const token = this.token
        if (!token) {
            throw new ChatAPIError({
                code: 'unauthorized',
                message: `Missing token`,
            })
        }

        const profile: PortalProfile = JSON.parse(token)

        const newMessage: Message = {
            id: nanoid(),
            create_time: new Date(),
            data: request.message,
            // image_url: profile.imageURL,
            // nick: profile.nick,
            user_id: profile.publicId,
            optimistic_id: request.optimistic_id,
            reply_message_id: request.reply_message_id,
            reply_user_id: this.messages.find(({ id }) => id === request.reply_message_id)?.user_id,
        }

        this.messages.push(newMessage)

        this.postChannelEvent(request.channel, {
            type: 'message_published',
            payload: newMessage,
        })

        return ok()
    })

    messageReport = createChatAPIEffect(async (_request: MessageReportRequest) => {
        await timeout(300)

        return ok()
    })

    messageDelete = createChatAPIEffect(async (request: MessageDeleteRequest) => {
        await timeout(300)

        const message = this.messages.find(({ id }) => id === request.message_id)
        if (!message) {
            throw new ChatAPIError({
                code: 'message_not_found',
                message: 'Message not found',
            })
        }

        this.messages = this.messages.filter(({ id }) => id !== request.message_id)

        if (request.ban_user) {
            this.postSystemEvent('system:admin', {
                type: 'user_muted',
                payload: {
                    reason_code: 'spam',
                    user_id: message.user_id,
                    create_time: new Date().toISOString(),
                    update_time: new Date().toISOString(),
                    expire_time: null,
                },
            })
        }

        if (request.delete_all_from_user) {
            this.postSystemEvent('system:user', {
                type: 'message_hidden_all',
                payload: {
                    user_id: message?.user_id ?? '',
                },
            })
        } else {
            this.postChannelEvent(request.channel, {
                type: 'message_hidden',
                payload: {
                    message_id: request.message_id,
                },
            })
        }

        return ok()
    })

    messageReactionAdd = createChatAPIEffect(async (request: MessageReactionRequest) => {
        await timeout(300)

        const profile: PortalProfile = JSON.parse(this.token!)

        this.postChannelEvent(request.channel, {
            type: 'message_reaction_added',
            payload: {
                emoji: request.emoji,
                message_id: request.message_id,
                user_id: profile.publicId,
            },
        })

        return ok()
    })

    messageReactionRemove = createChatAPIEffect(async (request: MessageReactionRequest) => {
        await timeout(300)

        const profile: PortalProfile = JSON.parse(this.token!)

        this.postChannelEvent(request.channel, {
            type: 'message_reaction_removed',
            payload: {
                emoji: request.emoji,
                message_id: request.message_id,
                user_id: profile.publicId,
            },
        })

        return ok()
    })

    messageTranslate = createChatAPIEffect(MessageTranslateResponseSchema, async (request: MessageTranslateRequest) => {
        await timeout(300)

        const translation: MessageTranslateResponse['translation'] = {}

        for (const messageId of request.message_id_list) {
            translation[messageId] = {
                message_id: messageId,
                translated_text: `Translated text for message ${messageId}`,
                detected_language_code: 'en',
            }
        }

        const response: MessageTranslateResponse = {
            translation,
        }

        return ok(response)
    })

    channelJoin = createChatAPIEffect(async (request: ChannelJoinRequest) => {
        await timeout(300)

        this.broadcastServer.postMessage({
            event: 'subscribed',
            payload: {
                channel: request.channel,
            },
        })

        return ok()
    })

    channelLeave = createChatAPIEffect(async (_request: ChannelLeaveRequest) => {
        await timeout(300)

        return ok()
    })

    channelHistory = createChatAPIEffect((_request: HistoryRequest) => {
        const response: HistoryResponse = {
            messages: [...this.messages],
            reaction_by_message_id: {
                '1324-5678-9012-3456': {
                    '👍': ['1330106016516787', '42'],
                    '😀': [
                        '42',
                        '43',
                        '44',
                        '45',
                        '46',
                        '47',
                        '48',
                        '49',
                        '50',
                        '51',
                        '52',
                        '53',
                        '54',
                        '55',
                        '56',
                        '57',
                        '58',
                        '59',
                        '60',
                        '61',
                        '62',
                        '63',
                        '64',
                        '65',
                        '66',
                        '67',
                        '68',
                        '69',
                        '70',
                        '71',
                    ],
                    '👎': ['42'],
                },
            },
        }

        return Promise.resolve(ok(response))
    })

    channelSlowmodeSet = createChatAPIEffect((request: ChannelSlowmodeRequest) => {
        const event: z.input<typeof SystemEventSchema> = {
            type: 'channel_slowmode',
            payload: {
                channel: request.channel,
                cooldown_seconds: mapIfDefined(request.cooldown, (cooldown) => toSeconds(cooldown)) ?? null,
            },
        }

        this.broadcastServer.postMessage({
            event: 'publication',
            payload: {
                channel: 'system:user',
                data: event,
            },
        })

        return Promise.resolve(ok())
    })

    private postChannelEvent = (channelName: string, event: ChannelEvent) => {
        this.broadcastServer.postMessage({
            event: 'publication',
            payload: {
                channel: channelName,
                data: event,
            },
        })
    }

    private postSystemEvent = (channelName: `system:${string}`, event: z.input<typeof SystemEventSchema>) => {
        this.broadcastServer.postMessage({
            event: 'publication',
            payload: {
                channel: channelName,
                data: event,
            },
        })
    }
}

const history: Message[] = [
    {
        id: '1111-2222-3333-4444',
        user_id: '2050106016516387',
        data: 'I\'m really sorry. Not that it helps, but like "the fire\'s out" and "the swelling\'s gone," it\'s just kind of nice to hear!',
        create_time: new Date(),
    },
    {
        id: 'aaaa-bbbb-cccc-dddd',
        user_id: '2050106016516387',
        data: 'Meet flame with flame! Attack!',
        create_time: new Date(),
    },
    {
        id: 'abcd-efgh-ijkl-mnop',
        user_id: '2',
        data: 'This world... fades...',
        create_time: new Date(),
        reply_user_id: '2050106016516387',
        reply_message_id: 'aaaa-bbbb-cccc-dddd',
    },
    {
        id: nanoid(),
        user_id: '2',
        data: 'Please, I request you group https://taonga.zendesk.com with me.',
        create_time: new Date(),
    },
    {
        id: nanoid(),
        user_id: '2050106016516387',
        data: 'That is possible.',
        create_time: new Date(),
    },
    {
        id: nanoid(),
        user_id: '2050106016516387',
        data: 'We must move on. Our destiny lies ahead.',
        create_time: new Date(),
    },
    {
        id: nanoid(),
        user_id: '2050106016516387',
        data: "I'm gonna rip your head off https://taonga.zendesk.com  and make you my puppet!",
        create_time: new Date(),
        optimistic_id: '42',
    },
    {
        id: 'aaaa-1111-cccc-dddd',
        user_id: '2',
        data: 'Ugh, feels like the time I drank that black mead...',
        create_time: new Date(),
    },
    {
        id: '1324-5678-9012-3456',
        user_id: '2',
        data: "Okay, everyone follow me and see how it's done.",
        create_time: new Date(),
        reply_message_id: '1111-2222-3333-4444',
        reply_user_id: '2050106016516387',
    },
    {
        id: nanoid(),
        user_id: '3',
        data: "What are we standing around for? Let's kill something already!",
        create_time: new Date(),
        reply_message_id: 'non-existent',
        reply_user_id: '2050106016516387',
    },
    {
        id: nanoid(),
        user_id: '3',
        data: 'Whorebagging son of a motherhumping... agh!',
        create_time: new Date(),
    },
    {
        id: nanoid(),
        user_id: '3',
        data: "Look at you! I've seen scarier things in old chamberpots!",
        create_time: new Date(),
        reply_message_id: 'aaaa-1111-cccc-dddd',
    },
    {
        id: nanoid(),
        user_id: '4',
        data: "Consider it done. 'Cause it is.",
        create_time: new Date(),
    },
].map((entry, idx) => ({
    ...entry,
    create_time: add(entry.create_time, {
        // days: idx,
        hours: idx * 7,
        minutes: idx,
        seconds: idx,
    }),
}))

const history2 = Array.from({ length: 100 }, (_, idx) =>
    history.map((message) => ({
        ...message,
        id: `${message.id}-${idx}`,
        user_id: `${message.user_id}-${idx}`,
        data: `${message.data}-${idx}`,
        reply_message_id: message.reply_message_id ? `${message.reply_message_id}-${idx}` : undefined,
        reply_user_id: message.reply_user_id ? `${message.reply_user_id}-${idx}` : undefined,
    }))
).flat()

function ok<T>(data: T): HTTPResponse<{ data: T }>
function ok<T>(data?: T): HTTPResponse<{ data: any }>
function ok<T>(data?: T): HTTPResponse<{ data: T } | { data: any }> {
    return {
        data: data ? { data } : { data: null },
        status: 200,
        headers: {},
    }
}
